import React, { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Bot,
  Sparkles,
  ShieldCheck,
  Globe,
  EyeOff,
  Briefcase,
  Headphones,
  GraduationCap,
  Rocket,
  Lock,
  MessageSquarePlus,
  Loader2,
  CheckCircle2,
} from "lucide-react";

// Future role catalogue showcased in the placeholder cards.
const FUTURE_ROLES = [
  {
    key: "sales",
    icon: Briefcase,
    title: "Sales AI Employee",
    blurb: "Discovery, pricing, and qualified handoffs — 24/7.",
  },
  {
    key: "product_specialist",
    icon: Sparkles,
    title: "Product Specialist",
    blurb: "Deep technical answers across your product surface.",
  },
  {
    key: "training",
    icon: GraduationCap,
    title: "Training Assistant",
    blurb: "Onboards new staff and answers process questions.",
  },
  {
    key: "launch",
    icon: Rocket,
    title: "Product Launch Assistant",
    blurb: "Talks about a new release with always-fresh context.",
  },
];

// Feedback options — MUST mirror `routes/feedback.py::ALLOWED_ASSISTANT_TYPES`.
const FEEDBACK_OPTIONS = [
  { key: "sales", label: "Sales AI Employee" },
  { key: "product_specialist", label: "Product Specialist" },
  { key: "training", label: "Training Assistant" },
  { key: "launch", label: "Product Launch Assistant" },
  { key: "support", label: "Customer Support Assistant" },
  { key: "other", label: "Other" },
];

// Map provisioning_status / lifecycle_state to a visual pill.
function statusPill(state) {
  const s = (state || "").toLowerCase();
  if (s === "ready" || s === "active") {
    return { dot: "bg-emerald-500", text: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  }
  if (s === "blocked" || s === "red_flagged") {
    return { dot: "bg-rose-500", text: "Blocked", cls: "bg-rose-50 text-rose-700 border-rose-200" };
  }
  if (s === "disabled" || s === "paused") {
    return { dot: "bg-stone-400", text: s === "paused" ? "Paused" : "Disabled", cls: "bg-stone-50 text-stone-700 border-stone-200" };
  }
  // anything else is in-flight: uploading / crawling / building_kb / generating_qa / running_tests / finalizing / draft / provisioning
  return { dot: "bg-amber-500 animate-pulse", text: "Provisioning", cls: "bg-amber-50 text-amber-700 border-amber-200" };
}

function roleLabel(role) {
  if (!role) return "General";
  return role
    .split("_")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

export default function AIEmployeesPreview() {
  const [primary, setPrimary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("sales");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedOk, setSubmittedOk] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/assistants")
      .then((r) => {
        if (!alive) return;
        const assistants = r.data?.assistants || [];
        // Pick the primary, fall back to first.
        const p = assistants.find((a) => a.is_primary) || assistants[0] || null;
        setPrimary(p);
      })
      .catch(() => {
        if (alive) setErrored(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Graceful no-op: if the endpoint failed (e.g. legacy tenant without an
  // assistants row yet because the migration is mid-rollout), don't render
  // anything. This component is preview-only and must never break the
  // dashboard.
  if (loading) return null;
  if (errored || !primary) return null;

  const pill = statusPill(primary.provisioning_status || primary.lifecycle_state);
  const visible = primary.public_visibility;

  return (
    <Card
      className="p-5 mb-6 border-emerald-100 bg-gradient-to-br from-emerald-50/40 via-white to-transparent"
      data-testid="ai-employees-preview"
    >
      {/* Section header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            <h2 className="font-display text-lg font-semibold" data-testid="ai-employees-title">
              AI Employees
            </h2>
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-wider border-emerald-200 text-emerald-700 bg-emerald-50"
              data-testid="ai-employees-platform-pill"
            >
              Multi-AI ready
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground max-w-xl">
            One platform, many specialists. Today you're running your primary assistant — soon you'll
            be able to deploy dedicated AI Employees for Sales, Support, Training, and more.
          </p>
        </div>
      </div>

      {/* Cards row */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Primary assistant card */}
        <Card
          className="p-4 bg-white border-emerald-200 shadow-[0_1px_0_rgba(16,185,129,0.06)] hover:shadow-md transition"
          data-testid="ai-employees-primary-card"
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-emerald-100 grid place-items-center text-emerald-700">
                <Bot className="w-4.5 h-4.5" />
              </div>
              <div className="min-w-0">
                <div
                  className="font-medium text-sm truncate max-w-[180px]"
                  title={primary.assistant_name}
                  data-testid="ai-employees-primary-name"
                >
                  {primary.assistant_name || "Primary Assistant"}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {roleLabel(primary.assistant_role)} · Primary
                </div>
              </div>
            </div>
            <span
              className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border inline-flex items-center gap-1 ${pill.cls}`}
              data-testid="ai-employees-primary-status"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${pill.dot}`} />
              {pill.text}
            </span>
          </div>

          <div className="flex items-center justify-between text-[11px] mt-3 pt-3 border-t border-dashed border-emerald-100">
            <span
              className={`inline-flex items-center gap-1 ${
                visible ? "text-emerald-700" : "text-muted-foreground"
              }`}
              data-testid="ai-employees-primary-visibility"
            >
              {visible ? (
                <>
                  <Globe className="w-3 h-3" /> Public link active
                </>
              ) : (
                <>
                  <EyeOff className="w-3 h-3" /> Not yet public
                </>
              )}
            </span>
            <span className="text-muted-foreground tabular-nums text-[10px] uppercase tracking-wider">
              v1
            </span>
          </div>
        </Card>

        {/* Add AI Employee — soft-disabled teaser */}
        <Card
          className="p-4 border-dashed border-emerald-200 bg-emerald-50/40 cursor-not-allowed select-none relative overflow-hidden"
          aria-disabled="true"
          data-testid="ai-employees-add-card"
        >
          <div className="absolute top-2 right-2">
            <Badge
              variant="outline"
              className="text-[9px] uppercase tracking-wider border-emerald-300 text-emerald-700 bg-white/70 backdrop-blur-sm"
              data-testid="ai-employees-coming-soon-pill"
            >
              <Lock className="w-2.5 h-2.5 mr-1" />
              Coming Soon
            </Badge>
          </div>
          <div className="flex items-center gap-2 mb-3 opacity-80">
            <div className="w-9 h-9 rounded-lg bg-white border border-dashed border-emerald-300 grid place-items-center text-emerald-700">
              <Sparkles className="w-4.5 h-4.5" />
            </div>
            <div>
              <div className="font-medium text-sm">+ Add AI Employee</div>
              <div className="text-[11px] text-muted-foreground">
                Deploy a second specialist
              </div>
            </div>
          </div>
          <p className="text-[11px] text-emerald-900/70 leading-relaxed">
            Spin up dedicated assistants for different roles — each with its own knowledge scope,
            tone, and public URL. Shared resources stay company-wide.
          </p>
        </Card>

        {/* Future role gallery — collapses into 3rd column then wraps below */}
        <Card
          className="p-4 border-emerald-100 bg-white/70"
          data-testid="ai-employees-future-card"
        >
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 mb-2 font-medium flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Future AI Employees
          </div>
          <ul className="space-y-2">
            {FUTURE_ROLES.map((r) => {
              const Icon = r.icon;
              return (
                <li
                  key={r.key}
                  className="flex items-start gap-2"
                  data-testid={`ai-employees-future-${r.key}`}
                >
                  <Icon className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs font-medium leading-tight">{r.title}</div>
                    <div className="text-[10.5px] text-muted-foreground leading-snug">
                      {r.blurb}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      {/* Footnote — clickable CTA opens the feedback modal */}
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10.5px] text-muted-foreground flex items-center gap-1.5">
          <Headphones className="w-3 h-3" />
          Tell us which AI Employee you'd want first — feedback shapes the rollout order.
        </div>
        <button
          type="button"
          onClick={() => {
            setSubmittedOk(false);
            setNote("");
            setSelectedType("sales");
            setFeedbackOpen(true);
          }}
          className="text-[11px] font-medium inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 hover:underline underline-offset-2"
          data-testid="ai-employees-feedback-cta"
        >
          <MessageSquarePlus className="w-3 h-3" />
          Share feedback
        </button>
      </div>

      {/* Feedback modal */}
      <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
        <DialogContent
          className="max-w-md"
          data-testid="ai-employees-feedback-modal"
        >
          {submittedOk ? (
            <div className="py-4 text-center" data-testid="ai-employees-feedback-thanks">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
              <DialogTitle className="text-lg font-display">Thank you</DialogTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Your feedback helps us prioritize upcoming AI Employee roles.
              </p>
              <Button
                className="mt-5"
                onClick={() => setFeedbackOpen(false)}
                data-testid="ai-employees-feedback-close-btn"
              >
                Close
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">
                  Which AI Employee should we ship next?
                </DialogTitle>
                <DialogDescription>
                  Pick the role most useful for your team. We use this to prioritize the rollout.
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                {FEEDBACK_OPTIONS.map((opt) => {
                  const selected = selectedType === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSelectedType(opt.key)}
                      className={`text-left text-xs px-3 py-2.5 rounded-lg border transition ${
                        selected
                          ? "border-emerald-500 bg-emerald-50 text-emerald-900 ring-1 ring-emerald-300"
                          : "border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/40"
                      }`}
                      data-testid={`ai-employees-feedback-opt-${opt.key}`}
                    >
                      <div className="flex items-center gap-1.5">
                        {selected && (
                          <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                        )}
                        <span className="font-medium">{opt.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-1">
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
                  Add a note (optional, max 500 chars)
                </label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 500))}
                  rows={3}
                  placeholder="Tell us how you'd use this role…"
                  data-testid="ai-employees-feedback-note"
                  className="text-sm"
                />
                <div className="text-[10px] text-muted-foreground text-right mt-1 tabular-nums">
                  {note.length}/500
                </div>
              </div>

              <DialogFooter className="mt-2">
                <Button
                  variant="ghost"
                  onClick={() => setFeedbackOpen(false)}
                  disabled={submitting}
                  data-testid="ai-employees-feedback-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  disabled={submitting}
                  onClick={async () => {
                    setSubmitting(true);
                    try {
                      const { data } = await api.post(
                        "/feedback/assistant-interest",
                        { assistant_type: selectedType, note: note || undefined },
                      );
                      if (data?.duplicate) {
                        toast.info(
                          data.message ||
                            "You have already submitted feedback recently. Thank you.",
                        );
                        setFeedbackOpen(false);
                      } else {
                        setSubmittedOk(true);
                      }
                    } catch (err) {
                      const msg =
                        err?.response?.data?.detail ||
                        "Could not submit feedback. Try again in a moment.";
                      toast.error(msg);
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  data-testid="ai-employees-feedback-submit-btn"
                >
                  {submitting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  Submit feedback
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
